/**
 * Date and time utility functions
 */

export type DateString = string // ISO date string
export type DateInput = Date | string

// Month names constant
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/**
 * Get UTC short date in MM/DD/YY format
 */
export function getUTCShortDate(isoDateStr: DateString): string {
  const date = new Date(isoDateStr)
  const month = date.getUTCMonth() + 1

  return `${month}/${date.getUTCDate()}/${date.getUTCFullYear().toString().slice(-2)}`
}

/**
 * Get short date in MM/DD/YY format
 */
export function getShortDate(isoDateStr: DateString): string {
  const date = new Date(isoDateStr)
  const month = date.getMonth() + 1

  return `${month}/${date.getDate()}/${date.getFullYear().toString().slice(-2)}`
}

/**
 * Get short human readable UTC date (e.g., "Jan 15, 2026")
 */
export function getShortHumanReadableUTCDate(isoDateStr: DateString): string {
  const date = new Date(isoDateStr)
  return `${MONTH_NAMES[date.getUTCMonth()].slice(0, 3)} ${date.getUTCDate()}, ${date.getUTCFullYear()}`
}

/**
 * Get short human readable date (e.g., "15 Jan, 2026")
 */
export function getShortHumanReadableDate(isoDateStr: DateString): string {
  const date = new Date(isoDateStr)
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)}, ${date.getFullYear()}`
}

/**
 * Get human readable date with optional prefix month
 */
export function getHumanReadableDate(isoDateStr: DateString, prefixMonth: boolean = false): string {
  const date = new Date(isoDateStr)
  if (prefixMonth) {
    return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
  }
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]}, ${date.getFullYear()}`
}

/**
 * Get short time in 12-hour format (e.g., "2:30 pm")
 */
export function getShortTime(isoDateStr: DateString): string {
  const date = new Date(isoDateStr)
  const minutes = ('0' + date.getMinutes().toString()).slice(-2)
  const ampm = date.getHours() >= 12 ? 'pm' : 'am'
  let hours = date.getHours() % 12

  if (hours === 0) {
    hours = 12
  }

  return `${hours}:${minutes} ${ampm}`
}

/**
 * Get expiration date formatted for display
 */
export function getExpDate(isoDateStr: DateString, forCustomerPortal: boolean = false): string {
  const date = new Date(isoDateStr)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = forCustomerPortal ? date.getFullYear().toString() : date.getFullYear().toString().slice(-2)

  return `${month}/${year}`
}

/**
 * Calculate difference between two dates
 */
export function dateDifference(date1: DateInput, date2: DateInput): number {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2

  return Math.abs(d1.getTime() - d2.getTime())
}

/**
 * Format date string with optional time range
 */
export function formatDateString(
  dateStr: DateString,
  addTimeRange: boolean = false,
  onlyDate: boolean = false
): string {
  const date = new Date(dateStr)

  if (!date.getTime()) {
    return dateStr // Return original if invalid date
  }

  const month = date.toLocaleDateString(undefined, { month: 'long' })
  const day = date.toLocaleDateString(undefined, { day: 'numeric' })

  if (onlyDate) {
    return `${month} ${day}`
  }

  const year = date.getFullYear()
  let result = `${month} ${day}, ${year}`

  if (addTimeRange) {
    const now = new Date()
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)

    if (date > tomorrow) {
      const futureTime = date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      result += ` by ${futureTime}`
    }
  }

  return result
}
